import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Spinner } from 'app/components/shared/icons';
import { Field, Formik, FormikProps } from 'formik';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useEditComment from 'hooks/useEditComment';
import usePostComment from 'hooks/usePostComment';
import { MessageableType } from 'services/messages';
import { setTiptapEditorFocus } from 'store/reducers/global/global-reducer';
import * as Yup from 'yup';

import { IComment } from '../Comment';
import RichTextEditor from '../RichTextEditor';
import TiptapEditor from '../TipTapEditor';
import {
  SubmitCommentButton,
  SubmitCommentButtonText,
  SubmitCommentLoadingContainer,
  SubmitCommentWrapper,
} from './styled';

interface SubmitCommentProps {
  messageable_type: MessageableType;
  messageable_uuid_or_slug: string;
  parent_uuid?: string;
  onFinish?: (comment: IComment) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  initialValue?: string;
  editing?: boolean;
  message_uuid?: string;
  placeholder?: string;
  scrollIntoView?: boolean;
  autoFocus?: boolean;
  id?: string;
}

export default function SubmitComment({
  messageable_type,
  messageable_uuid_or_slug,
  parent_uuid = null,
  editing = false,
  message_uuid = null,
  onFinish,
  onFocus,
  onBlur,
  initialValue = '',
  placeholder = null,
  scrollIntoView = false,
  autoFocus = false,
  id = null,
}: SubmitCommentProps) {
  const dispatch = useDispatch();
  const messagePostedBehavioralFunction = useBehavioral(BEHAVIORALS.MESSAGE_POSTED);
  const messageEditedBehavioralFunction = useBehavioral(BEHAVIORALS.MESSAGE_EDITED);
  const { mutate: postComment, isLoading } = usePostComment();
  const { mutate: editComment, isLoading: isEditing } = useEditComment();
  const formikRef = useRef<FormikProps<any>>(null);
  const submitCommentWrapperRef = useRef<HTMLFormElement>(null);

  const commentValidationSchema = Yup.object().shape({
    comment: Yup.string().required('Comment is required'),
  });

  useEffect(() => {
    if (scrollIntoView) {
      submitCommentWrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      submitCommentWrapperRef.current?.querySelector('input')?.focus();
    }
  }, [scrollIntoView]);

  const handleSubmit = (values: any) => {
    if (editing) {
      editComment(
        {
          content: values.comment,
          message_uuid,
        },
        {
          onSuccess: (response: any) => {
            messageEditedBehavioralFunction({
              related_object_uuid: messageable_uuid_or_slug,
              related_object_type:
                messageable_type.charAt(0).toUpperCase() + messageable_type.slice(1),
              action_details: `Edited message`,
            });
            onFinish?.(response.data?.message);
            formikRef.current?.resetForm();
          },
          onSettled: () => {
            dispatch(setTiptapEditorFocus(false));
          },
        }
      );
    } else {
      postComment(
        {
          content: values.comment,
          messageable_type,
          messageable_uuid_or_slug,
          parent_uuid,
        },
        {
          onSuccess: (response) => {
            messagePostedBehavioralFunction({
              related_object_uuid: messageable_uuid_or_slug,
              related_object_type: messageable_type,
              action_details: `Posted message`,
            });
            onFinish?.(response.data);
            formikRef.current?.resetForm();
          },
          onSettled: () => {
            dispatch(setTiptapEditorFocus(false));
          },
        }
      );
    }
  };

  const handleOnFocus = () => {
    dispatch(setTiptapEditorFocus(true));
    onFocus?.();
  };

  const handleOnBlur = () => {
    dispatch(setTiptapEditorFocus(false));
    onBlur?.();
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      formikRef.current?.submitForm();
    }
  };

  return (
    <Formik
      initialValues={{
        comment: initialValue,
      }}
      validationSchema={commentValidationSchema}
      isInitialValid={false}
      validateOnChange
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {(props) => (
        <SubmitCommentWrapper
          onSubmit={props.handleSubmit}
          ref={submitCommentWrapperRef}
          data-id={parent_uuid ? `submit-comment-wrapper-${parent_uuid}` : 'submit-comment-wrapper'}
        >
          {!isLoading && !isEditing ? (
            <Field
              {...props}
              placeholder={placeholder}
              name="comment"
              component={RichTextEditor}
              onChange={props.handleChange}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              onKeyDown={handleOnKeyDown}
              autoFocus={scrollIntoView || autoFocus}
              id={id}
            />
          ) : (
            <SubmitCommentLoadingContainer>
              <Spinner width={24} height={24} fill="var(--color-primary)" />
            </SubmitCommentLoadingContainer>
          )}
          <SubmitCommentButton type="submit" disabled={!props.isValid || isLoading || isEditing}>
            <SubmitCommentButtonText>POST</SubmitCommentButtonText>
          </SubmitCommentButton>
        </SubmitCommentWrapper>
      )}
    </Formik>
  );
}
